import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Save, Plus, Trash2, ArrowLeft, Lock, Inbox } from "lucide-react";
import { applyContent } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

const inputCls =
    "w-full bg-[#0c0d10] border border-white/15 rounded-md px-3 py-2 text-sm text-neutral-200 placeholder:text-neutral-600 outline-none focus:border-vermilion transition-colors";

const TextField = ({ label, value, onChange, textarea, testid }) => (
    <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
        {textarea ? (
            <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} className={`${inputCls} mt-1 resize-y`} />
        ) : (
            <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`${inputCls} mt-1`} />
        )}
    </label>
);

const CsvField = ({ label, value, onChange, testid }) => (
    <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label} (comma separated)</span>
        <input
            value={(value || []).join(", ")}
            onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            data-testid={testid}
            className={`${inputCls} mt-1 font-mono text-xs`}
        />
    </label>
);

const fieldComponent = (f, item, update, prefix) =>
    f.csv ? (
        <CsvField key={f.k} label={f.label} value={item[f.k]} onChange={(v) => update({ ...item, [f.k]: v })} testid={`${prefix}-${f.k}`} />
    ) : (
        <TextField key={f.k} label={f.label} value={item[f.k]} onChange={(v) => update({ ...item, [f.k]: v })} textarea={f.textarea} testid={`${prefix}-${f.k}`} />
    );

const ListEditor = ({ items, fields, blank, onChange, section }) => (
    <div className="space-y-5">
        {(items || []).map((item, i) => (
            <div key={i} data-testid={`admin-${section}-item-${i}`} className="border border-white/10 rounded-lg p-4 bg-white/[0.02] relative">
                <button
                    data-testid={`admin-${section}-remove-${i}`}
                    onClick={() => onChange(items.filter((_, x) => x !== i))}
                    aria-label="Remove item"
                    className="absolute top-3 right-3 w-7 h-7 rounded-md border border-white/10 flex items-center justify-center text-neutral-500 hover:bg-vermilion hover:text-white hover:border-vermilion transition-colors"
                >
                    <Trash2 size={12} />
                </button>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-vermilion mb-3">
                    #{String(i + 1).padStart(2, "0")} — {item.title || item.role || item.label || "item"}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                    {fields.map((f) => (
                        <div key={f.k} className={f.textarea || f.csv || f.full ? "sm:col-span-2" : ""}>
                            {fieldComponent(f, item, (next) => onChange(items.map((x, xi) => (xi === i ? next : x))), `admin-${section}-${i}`)}
                        </div>
                    ))}
                </div>
            </div>
        ))}
        <button
            data-testid={`admin-${section}-add`}
            onClick={() => onChange([...(items || []), { ...blank }])}
            className="inline-flex items-center gap-2 rounded-md border border-dashed border-white/25 px-4 py-2.5 text-xs font-mono uppercase tracking-wider text-neutral-400 hover:border-vermilion hover:text-vermilion transition-colors"
        >
            <Plus size={13} /> Add entry
        </button>
    </div>
);

const SECTIONS = [
    { key: "identity", title: "Identity", type: "object", fields: [
        { k: "name", label: "Name" }, { k: "handle", label: "Handle" }, { k: "role", label: "Role" },
        { k: "location", label: "Location" }, { k: "email", label: "Email" }, { k: "status", label: "Status", full: true },
        { k: "tagline", label: "Tagline", full: true }, { k: "punchline", label: "Punchline", full: true },
        { k: "resume", label: "Resume URL" }, { k: "github", label: "GitHub URL" }, { k: "linkedin", label: "LinkedIn URL" },
    ]},
    { key: "images", title: "Images", type: "object", fields: [
        { k: "portrait", label: "Portrait URL", full: true }, { k: "portraitFallback", label: "Portrait Fallback URL", full: true },
    ]},
    { key: "stats", title: "Stats", type: "list", blank: { label: "", value: "", unit: "", detail: "" }, fields: [
        { k: "label", label: "Label" }, { k: "value", label: "Value" }, { k: "unit", label: "Unit" }, { k: "detail", label: "Detail" },
    ]},
    { key: "experience", title: "Experience", type: "list", blank: { period: "", role: "", company: "", description: "", tags: [] }, fields: [
        { k: "period", label: "Period" }, { k: "role", label: "Role" }, { k: "company", label: "Company", full: true },
        { k: "description", label: "Description", textarea: true }, { k: "tags", label: "Tags", csv: true },
    ]},
    { key: "projects", title: "Projects", type: "list", blank: { id: "PRJ-13", number: "13", title: "", category: "", filter: "web", client: "", description: "", image: "", fallback: "", tags: [] }, fields: [
        { k: "id", label: "ID (PRJ-xx)" }, { k: "number", label: "Number" }, { k: "title", label: "Title", full: true },
        { k: "category", label: "Category" }, { k: "filter", label: "Filter key (infrastructure/web/network/observability/cloud)" },
        { k: "client", label: "Client", full: true }, { k: "description", label: "Description", textarea: true },
        { k: "image", label: "Image URL", full: true }, { k: "fallback", label: "Fallback Image URL", full: true },
        { k: "tags", label: "Tags", csv: true },
    ]},
    { key: "certificates", title: "Certificates", type: "list", blank: { id: "CERT-08", title: "", issuer: "", category: "", image: "", link: "" }, fields: [
        { k: "id", label: "ID (CERT-xx)" }, { k: "issuer", label: "Issuer" }, { k: "title", label: "Title", full: true },
        { k: "category", label: "Category" }, { k: "image", label: "Image URL" },
        { k: "link", label: "Verify / View Link", full: true },
    ]},
    { key: "skills", title: "Skills", type: "list", blank: { number: "07", title: "", items: [] }, fields: [
        { k: "number", label: "Number" }, { k: "title", label: "Title" }, { k: "items", label: "Items", csv: true },
    ]},
    { key: "services", title: "Services", type: "csvList" },
    { key: "messages", title: "Inbox", type: "messages" },
];

export default function AdminPage() {
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loginErr, setLoginErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [doc, setDoc] = useState(null);
    const [messages, setMessages] = useState([]);
    const [tab, setTab] = useState("identity");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => setUser(r.data)).catch(() => setUser(false));
    }, []);

    useEffect(() => {
        if (!user) return;
        axios.get(`${API}/content`).then((r) => setDoc(r.data)).catch(() => toast.error("Failed to load content"));
        axios.get(`${API}/contact`, { withCredentials: true }).then((r) => setMessages(r.data)).catch(() => {});
    }, [user]);

    const login = async (e) => {
        e.preventDefault();
        setBusy(true);
        setLoginErr("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
            setUser(data);
        } catch (err) {
            setLoginErr(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
        setUser(false);
        setDoc(null);
    };

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/content`, { data: doc }, { withCredentials: true });
            applyContent(doc);
            toast.success("Content saved — desktop updated.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (user === null) {
        return <div className="min-h-screen bg-[#14161c] flex items-center justify-center font-mono text-xs text-neutral-500">checking session…</div>;
    }

    if (user === false) {
        return (
            <div className="min-h-screen bg-[#14161c] flex items-center justify-center p-4" data-testid="admin-login-page">
                <div className="w-full max-w-sm rounded-xl border border-white/10 bg-[#1a1d24] p-8 shadow-2xl">
                    <div className="flex items-center gap-3 mb-8">
                        <img src="/logo.png" alt="" className="w-10 h-10 rounded-lg" />
                        <div>
                            <div className="font-bold text-white text-sm">wikiOS — root console</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">authorized access only</div>
                        </div>
                    </div>
                    <form onSubmit={login} data-testid="admin-login-form" className="space-y-4">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email</span>
                            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" placeholder="root@wikios" className={`${inputCls} mt-1`} />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Password</span>
                            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" placeholder="••••••••" className={`${inputCls} mt-1`} />
                        </label>
                        {loginErr && (
                            <div data-testid="admin-login-error" className="rounded-md border border-vermilion/40 bg-vermilion/10 px-3 py-2 font-mono text-xs text-vermilion">
                                {loginErr}
                            </div>
                        )}
                        <button type="submit" disabled={busy} data-testid="admin-login-button" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-vermilion text-white py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#c93327] transition-colors disabled:opacity-50">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />}
                            {busy ? "Authenticating…" : "sudo login"}
                        </button>
                    </form>
                    <Link to="/" data-testid="admin-back-home" className="mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] text-neutral-500 hover:text-neutral-300 transition-colors">
                        <ArrowLeft size={12} /> back to desktop
                    </Link>
                </div>
            </div>
        );
    }

    const section = SECTIONS.find((s) => s.key === tab);

    return (
        <div className="min-h-screen bg-[#14161c] text-neutral-200" data-testid="admin-dashboard">
            <header className="h-14 border-b border-white/10 bg-[#0c0d10] flex items-center justify-between px-4 sm:px-6">
                <div className="flex items-center gap-3">
                    <img src="/logo.png" alt="" className="w-6 h-6 rounded" />
                    <span className="font-bold text-sm text-white">wikiOS root console</span>
                    <span className="hidden sm:inline font-mono text-[10px] text-neutral-500">{user.email}</span>
                </div>
                <div className="flex items-center gap-2">
                    {doc && tab !== "messages" && (
                        <button onClick={save} disabled={saving} data-testid="admin-save-button" className="inline-flex items-center gap-2 rounded-md bg-vermilion text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#c93327] transition-colors disabled:opacity-50">
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                            {saving ? "Saving…" : "Save"}
                        </button>
                    )}
                    <Link to="/" data-testid="admin-view-site" className="rounded-md border border-white/15 px-3 py-2 font-mono text-[11px] text-neutral-400 hover:text-white transition-colors">
                        view desktop
                    </Link>
                    <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-2 font-mono text-[11px] text-neutral-400 hover:text-vermilion hover:border-vermilion transition-colors">
                        <LogOut size={12} /> logout
                    </button>
                </div>
            </header>
            <div className="flex flex-col md:flex-row min-h-[calc(100vh-3.5rem)]">
                <aside className="md:w-52 shrink-0 border-b md:border-b-0 md:border-r border-white/10 p-3 flex md:flex-col gap-1 overflow-x-auto">
                    {SECTIONS.map((s) => (
                        <button
                            key={s.key}
                            data-testid={`admin-tab-${s.key}`}
                            onClick={() => setTab(s.key)}
                            className={`shrink-0 text-left rounded-md px-3 py-2 text-xs font-medium transition-colors whitespace-nowrap ${
                                tab === s.key ? "bg-vermilion text-white" : "text-neutral-400 hover:bg-white/5 hover:text-neutral-200"
                            }`}
                        >
                            {s.title}
                        </button>
                    ))}
                </aside>
                <main className="flex-1 p-4 sm:p-8 overflow-y-auto">
                    {!doc ? (
                        <div className="font-mono text-xs text-neutral-500">loading content…</div>
                    ) : section.type === "messages" ? (
                        <div data-testid="admin-messages">
                            <h2 className="font-bold text-lg text-white mb-5 inline-flex items-center gap-2"><Inbox size={17} /> Inbox — {messages.length} messages</h2>
                            <div className="space-y-3">
                                {messages.length === 0 && <p className="font-mono text-xs text-neutral-500">no messages yet.</p>}
                                {[...messages].reverse().map((m) => (
                                    <div key={m.id} data-testid={`admin-message-${m.id.slice(0, 8)}`} className="border border-white/10 rounded-lg p-4 bg-white/[0.02]">
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                            <span className="text-sm font-semibold text-white">{m.name} <span className="font-mono text-xs text-neutral-500">&lt;{m.email}&gt;</span></span>
                                            <span className="font-mono text-[10px] text-neutral-500">{new Date(m.timestamp).toLocaleString("en-GB")}</span>
                                        </div>
                                        <p className="text-sm text-neutral-400 whitespace-pre-wrap">{m.message}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : section.type === "object" ? (
                        <div className="max-w-3xl">
                            <h2 className="font-bold text-lg text-white mb-5">{section.title}</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {section.fields.map((f) => (
                                    <div key={f.k} className={f.full ? "sm:col-span-2" : ""}>
                                        <TextField
                                            label={f.label}
                                            value={doc[section.key]?.[f.k]}
                                            onChange={(v) => setDoc({ ...doc, [section.key]: { ...doc[section.key], [f.k]: v } })}
                                            textarea={f.textarea}
                                            testid={`admin-${section.key}-${f.k}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : section.type === "csvList" ? (
                        <div className="max-w-2xl">
                            <h2 className="font-bold text-lg text-white mb-5">{section.title}</h2>
                            <CsvField
                                label="Services offered"
                                value={doc.services}
                                onChange={(v) => setDoc({ ...doc, services: v })}
                                testid="admin-services-input"
                            />
                        </div>
                    ) : (
                        <div className="max-w-4xl">
                            <h2 className="font-bold text-lg text-white mb-5">{section.title}</h2>
                            <ListEditor
                                items={doc[section.key]}
                                fields={section.fields}
                                blank={section.blank}
                                section={section.key}
                                onChange={(items) => setDoc({ ...doc, [section.key]: items })}
                            />
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
